import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { gsap } from "gsap";
import { Arrow } from "./Arrow";
import { CaseStudyCarousel } from "./CaseStudyCarousel";

export function CaseStudy({ project, onClose }) {
  const rootRef = useRef(null);
  const closeRef = useRef(null);
  const projectImages = project.images ?? [
    {
      src: project.image,
      alt: project.imageAlt,
      width: project.imageWidth,
      height: project.imageHeight,
    },
  ];

  useLayoutEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const isTestEnvironment = navigator.userAgent.toLowerCase().includes("jsdom");
    if (reduceMotion || isTestEnvironment) return undefined;

    const context = gsap.context(() => {
      gsap.fromTo(rootRef.current, { clipPath: "inset(0 0 100% 0)" }, { clipPath: "inset(0 0 0% 0)", duration: 0.72, ease: "expo.inOut" });
      gsap.from("[data-case-reveal]", { y: 28, opacity: 0, duration: 0.62, stagger: 0.06, ease: "power4.out", delay: 0.42 });
    }, rootRef);

    return () => context.revert();
  }, []);

  useEffect(() => {
    const previousFocus = document.activeElement;
    document.body.classList.add("case-study-open");
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (document.querySelector(".image-lightbox")) return;
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;

      const focusable = Array.from(rootRef.current.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("case-study-open");
      previousFocus?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <aside className="case-study" ref={rootRef} role="dialog" aria-modal="true" aria-labelledby={`case-study-${project.number}`} data-lenis-prevent>
      <header className="case-study__bar">
        <span>KQ / Case study {project.number}</span>
        <button className="case-study__close" type="button" onClick={onClose} ref={closeRef} aria-label="Close case study">
          <span />
          <span />
        </button>
      </header>

      <div className="page-width case-study__inner">
        <section className="case-study__hero">
          <div data-case-reveal>
            <small>{project.eyebrow}</small>
            <h2 id={`case-study-${project.number}`}>{project.title}</h2>
          </div>
          <p data-case-reveal>{project.caseStudy.intro}</p>
        </section>

        <div className="case-study__media" data-case-reveal>
          <CaseStudyCarousel images={projectImages} title={project.title} />
        </div>

        <div className="case-study__details">
          <section data-case-reveal>
            <small>Challenge</small>
            <p>{project.caseStudy.challenge}</p>
          </section>
          <section data-case-reveal>
            <small>Approach</small>
            <ul>
              {project.caseStudy.approach.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>
          <section data-case-reveal>
            <small>Result</small>
            <p>{project.caseStudy.result}</p>
          </section>
        </div>

        <footer className="case-study__footer" data-case-reveal>
          <div>{project.stack.map((item) => <span key={item}>{item}</span>)}</div>
          {project.href && (
            <a href={project.href} target="_blank" rel="noreferrer">
              {project.linkLabel ?? "Visit live project"} <Arrow />
            </a>
          )}
        </footer>
      </div>
    </aside>,
    document.body
  );
}
